import { useEffect } from 'react';
import socketService, { EnhancedChatMessage } from '../services/socketService';
import { logMessage } from '../utils/logMessage';

interface UseAIResponseProps {
  speechCredentials: { token: string; region: string } | null;
  assessment: any;
  contactName: string;
  onTranscript: (text: string, speaker: string) => void;
  onFirstAIVoice: () => void;
  setBlockMic: (b: boolean) => void;
  speakTextWithAzureSDK: Function;
  playAudio: Function;
  setSocketError: (msg: string | null) => void;
  setUserMuted: (b: boolean) => void;
  setBadState: (b: boolean) => void;
  fetchSpeechToken: Function;
  setSpeechCredentials: Function;
  recognitionTimestampsRef: React.MutableRefObject<Map<string, number>>;
  aiRequestTimestampsRef: React.MutableRefObject<Map<string, number>>;
  pendingAIResponseRef: React.MutableRefObject<EnhancedChatMessage | null>;
  lastMessageRef: React.MutableRefObject<EnhancedChatMessage | null>;
}

function base64ToArrayBuffer(base64: string): ArrayBuffer {
  const base64Clean = base64.replace(/^data:audio\/\w+;base64,/, '');
  const binaryString = globalThis.window.atob(base64Clean);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.codePointAt(i) ?? 0;
  }
  return bytes.buffer;
}

function areSpeechCredentialsReady(creds: { token: string; region: string } | null): boolean {
  return (
    !!creds &&
    typeof creds.token === 'string' &&
    creds.token.length > 0 &&
    typeof creds.region === 'string' &&
    creds.region.length > 0
  );
}

export function useAIResponse({
  speechCredentials,
  assessment,
  contactName,
  onTranscript,
  onFirstAIVoice,
  setBlockMic,
  speakTextWithAzureSDK,
  playAudio,
  setSocketError,
  setUserMuted,
  setBadState,
  fetchSpeechToken,
  setSpeechCredentials,
  recognitionTimestampsRef,
  aiRequestTimestampsRef,
  pendingAIResponseRef,
  lastMessageRef,
}: UseAIResponseProps) {
  useEffect(() => {
    function processResponse(data: EnhancedChatMessage) {
      lastMessageRef.current = data;
      if (data.conversation_id) {
        if (!data.message || data.message === "") {
          setBlockMic(false);
          return;
        }
        const aiGenerationStart = aiRequestTimestampsRef.current.get(data.conversation_id);
        if (aiGenerationStart) {
          aiRequestTimestampsRef.current.delete(data.conversation_id);
        }
      }
      logMessage('[AIResponse] Triggering AI response request', 'info')
      onTranscript(data.message, contactName);

      if (data.audio_data) {
        playAudio(base64ToArrayBuffer(data.audio_data));
      } else if (areSpeechCredentialsReady(speechCredentials)) {
        const recognitionTimestamp = data.conversation_id
          ? recognitionTimestampsRef.current.get(data.conversation_id)
          : undefined;
        logMessage('[AIResponse] Requesting response from speech service', 'info')
        speakTextWithAzureSDK({
          text: data.message,
          token: speechCredentials!.token,
          region: speechCredentials!.region,
          voiceName: assessment.voiceName || "en-US-AvaMultilingualNeural",
          onEnd: () => {
            setBlockMic(false);
            setSocketError(null);
          },
          onError: () => {
            setUserMuted(true);
            setBlockMic(false);
          },
          recognitionTimestamp,
          setBadState,
          fetchSpeechToken,
          setSpeechCredentials,
        });
      }

      if (data.conversation_id) {
        recognitionTimestampsRef.current.delete(data.conversation_id);
      }
      onFirstAIVoice?.();
    }

    function handleAIResponse(data: EnhancedChatMessage) {
      if (!areSpeechCredentialsReady(speechCredentials) && !data.audio_data) {
        pendingAIResponseRef.current = data;
        return;
      }
      processResponse(data);
    }

    if (areSpeechCredentialsReady(speechCredentials) && pendingAIResponseRef.current) {
      const pending = pendingAIResponseRef.current;
      pendingAIResponseRef.current = null;
      processResponse(pending);
    }

    socketService.onAIResponse(handleAIResponse);
    return () => {
      socketService.socket?.off('ai_response', handleAIResponse);
    };
  }, [
    speechCredentials,
    assessment.voiceName,
    contactName,
    onTranscript,
    onFirstAIVoice,
    setBlockMic,
    speakTextWithAzureSDK,
    playAudio,
    setSocketError,
    setUserMuted,
    setBadState,
    fetchSpeechToken,
    setSpeechCredentials,
    recognitionTimestampsRef,
    aiRequestTimestampsRef,
    pendingAIResponseRef,
    lastMessageRef,
  ]);
}